import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import TicketChat from '../../components/TicketChat.jsx';

export default function Tickets() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState('active');
  // Task #108 — admin manual ticket dispatch (phone-in)
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [skills, setSkills] = useState([]);
  // CHAT/AUDIT drawer (spec-ticket-chat.md)
  const [detailTicket, setDetailTicket] = useState(null);
  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(collection(db, 'vendors', vendorId, 'tickets'), orderBy('createdAt', 'desc')),
      (snap) => setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [vendorId]);

  const visible = (tickets || []).filter((t) => {
    if (filter === 'all') return true;
    return ['NEW', 'ROUTING'].includes(t.status) || t.status?.startsWith('RINGING_') || t.status === 'ASSIGNED';
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Tickets</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setNewTicketOpen(true)} className="btn-primary">
            + New ticket (phone-in)
          </button>
          <span style={{ width: 8 }} />
          <button onClick={() => setFilter('active')} className={filter === 'active' ? 'btn-chip-active' : 'btn-chip'}>Active</button>{' '}
          <button onClick={() => setFilter('all')} className={filter === 'all' ? 'btn-chip-active' : 'btn-chip'}>All</button>
        </div>
      </div>

      {newTicketOpen && (
        <AdminCreateTicketModal
          skills={skills}
          onClose={() => setNewTicketOpen(false)}
        />
      )}

      {tickets === null && <div className="placeholder">Loading...</div>}
      {tickets !== null && visible.length === 0 && (
        <div className="placeholder">{filter === 'active' ? 'No active tickets right now.' : 'No tickets yet.'}</div>
      )}
      {visible.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Requester</th>
              <th>Site</th>
              <th>Skill</th>
              <th>Submitted</th>
              <th>Window</th>
              <th>Assigned to</th>
              <th>Feedback</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((t) => (
              <tr key={t.id}>
                <td><StatusBadge status={t.status} /></td>
                <td>{t.customerName}</td>
                <td>
                  {t.siteName || <span style={{ color: 'var(--muted)' }}>-</span>}
                  {t.address && (
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>📍 {t.address}</div>
                  )}
                  {t.intakeMode === 'qr-walkup' && (
                    <div style={{ fontSize: 11, color: '#ea580c' }}>
                      QR · {t.accessPointLabel || 'walk-up'}
                    </div>
                  )}
                </td>
                <td>{t.skillName}</td>
                <td>{t.createdAt?.toDate?.().toLocaleString() || ''}</td>
                <td><Countdown expiresAt={t.pendingTimerExpiresAt} status={t.status} /></td>
                <td>{t.assignedTo?.name || <span style={{ color: 'var(--muted)' }}>-</span>}</td>
                <td><FeedbackCell t={t} /></td>
                <td>
                  <button className="btn-chip" onClick={() => setDetailTicket(t)}>
                    View{t.chat?.count ? ` · 💬${t.chat.count}` : ''}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detailTicket && (
        <TicketDetailDrawer
          vendorId={vendorId}
          t={detailTicket}
          onClose={() => setDetailTicket(null)}
        />
      )}
    </>
  );
}

// ============================================================================
// TicketDetailDrawer — CHAT + AUDIT (spec-ticket-chat.md, 2026-07-09).
// Two tabs: the full customer↔tech conversation (read-only for admins) and the
// dispatch log — every ring, skip, escalation, and accept, timestamped. This is
// the on-screen SLA receipt the landing page promises.
// ============================================================================

function TicketDetailDrawer({ vendorId, t, onClose }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('conversation');
  const [log, setLog] = useState(null);

  useEffect(() => {
    if (tab !== 'log' || !vendorId) return;
    return onSnapshot(
      query(
        collection(db, 'vendors', vendorId, 'tickets', t.id, 'escalationLog'),
        orderBy('createdAt', 'asc'),
      ),
      (snap) => setLog(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setLog([]),
    );
  }, [tab, vendorId, t.id]);

  if (tab === 'conversation') {
    return (
      <TicketChat
        vendorId={vendorId}
        ticket={t}
        me={{ uid: user?.uid || 'admin', name: 'Administrator', role: 'vendor_admin' }}
        onClose={onClose}
        readOnly
        footer={
          <button className="btn-ghost" style={{ width: '100%' }} onClick={() => setTab('log')}>
            📋 View dispatch log
          </button>
        }
      />
    );
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ margin: 0 }}>📋 Dispatch log · {t.skillName}</h3>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 13 }}>Close</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', margin: '12px 0' }}>
          {log === null && <div className="placeholder">Loading…</div>}
          {log && log.length === 0 && <div className="placeholder">No dispatch events recorded.</div>}
          {(log || []).map((e) => (
            <div key={e.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13.5 }}>
              <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {e.createdAt?.toDate?.().toLocaleString() || ''}
              </span>
              <span>
                <strong>{labelForLogAction(e.action)}</strong>
                {e.level != null && ` · L${e.level}`}
                {e.toLevel != null && ` → L${e.toLevel}`}
                {e.actorName && ` · ${e.actorName}`}
                {e.reason && <span style={{ color: 'var(--muted)' }}> — {e.reason}</span>}
              </span>
            </div>
          ))}
        </div>
        <button className="btn-ghost" style={{ width: '100%' }} onClick={() => setTab('conversation')}>
          💬 View conversation
        </button>
      </div>
    </div>
  );
}

function labelForLogAction(action) {
  const map = {
    'ringing': 'Rang responders',
    'ringing-with-fallback': 'Rang responders (vendor-wide fallback)',
    'level-skipped-no-coverage': 'Level skipped — nobody available',
    'escalated': 'Escalated',
    'accepted': 'Accepted',
    'rejected': 'Rejected / skipped',
    'abandoned': 'Abandoned',
    'coverage-gap-detected': 'Coverage gap detected',
    'reception-forwarded': 'Forwarded to reception',
    'closed': 'Closed',
  };
  return map[action] || action;
}

function StatusBadge({ status }) {
  let color = '#64748b', label = status || '-';
  if (status === 'NEW' || status === 'ROUTING') { label = 'Routing...'; color = '#f59e0b'; }
  else if (status?.startsWith('RINGING_')) { label = `Ringing ${status.replace('RINGING_', '')}`; color = '#f59e0b'; }
  else if (status === 'ASSIGNED') { label = 'Assigned'; color = '#10b981'; }
  else if (status === 'CLOSED') { label = 'Closed'; color = '#64748b'; }
  else if (status === 'ABANDONED') { label = 'Abandoned'; color = '#dc2626'; }
  return <span className="badge" style={{ background: 'transparent', border: `1px solid ${color}`, color }}>{label}</span>;
}

function FeedbackCell({ t }) {
  const cust = t.feedback?.customer;
  const resp = t.feedback?.responder;
  if (!cust && !resp) {
    if (t.status === 'CLOSED' && t.feedbackRequested) {
      return <span style={{ color: 'var(--muted)', fontSize: 12 }}>awaiting</span>;
    }
    return <span style={{ color: 'var(--muted)' }}>-</span>;
  }
  const tooltipParts = [];
  if (cust) {
    tooltipParts.push(`${cust.rating}/5 from requester`);
    if (cust.comment) tooltipParts.push(`"${cust.comment}"`);
    if (cust.wouldRecommend === true) tooltipParts.push('Would call again: yes');
    if (cust.wouldRecommend === false) tooltipParts.push('Would call again: no');
  }
  if (resp?.note) tooltipParts.push(`Responder note: "${resp.note}"`);
  return (
    <span title={tooltipParts.join('\n')} style={{ fontSize: 13 }}>
      {cust && (
        <span style={{ color: '#f59e0b', marginRight: 4 }}>
          {'★'.repeat(cust.rating)}{'☆'.repeat(5 - cust.rating)}
        </span>
      )}
      {resp?.note && <span style={{ color: '#0369a1' }}>note</span>}
    </span>
  );
}

function Countdown({ expiresAt, status }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!status?.startsWith('RINGING_')) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);
  if (!status?.startsWith('RINGING_') || !expiresAt) {
    return <span style={{ color: 'var(--muted)' }}>-</span>;
  }
  const ms = (expiresAt.toDate?.() || expiresAt).getTime() - now;
  if (ms <= 0) return <span style={{ color: '#dc2626' }}>0:00</span>;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return <span>{m}:{String(s).padStart(2, '0')}</span>;
}

// ============================================================================
// AdminCreateTicketModal — Task #108 (INTERNAL-DISPATCH)
// Lets a vendor admin manually open a ticket from an incoming phone call when
// the caller doesn't have a Giraops customer account. Calls adminCreateTicket
// Cloud Function which creates the ticket with customerUid=null and runs it
// through the normal dispatch engine.
// ============================================================================

function AdminCreateTicketModal({ skills, onClose }) {
  const [skillId, setSkillId] = useState('');
  const [description, setDescription] = useState('');
  const [callerName, setCallerName] = useState('');
  const [callerPhone, setCallerPhone] = useState('');
  const [siteName, setSiteName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!skillId || !description.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      const fn = httpsCallable(functions, 'adminCreateTicket');
      await fn({
        skillCategoryId: skillId,
        description: description.trim(),
        callerName: callerName.trim() || undefined,
        callerPhone: callerPhone.trim() || undefined,
        siteName: siteName.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Could not create ticket.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 520,
          background: 'white', borderRadius: 12,
          padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ margin: 0 }}>New ticket (phone-in)</h3>
          <button type="button" onClick={onClose} className="btn-ghost" style={{ fontSize: 13 }}>Cancel</button>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>
          Take a service request from someone who doesn't have a Giraops account.
          The ticket will run through your normal escalation chain.
        </p>

        <label style={{ fontSize: 13, fontWeight: 600 }}>Caller phone</label>
        <input
          type="tel"
          value={callerPhone}
          onChange={(e) => setCallerPhone(e.target.value)}
          placeholder="+61 412 345 678"
          style={inputStyle}
          autoFocus
        />

        <label style={{ fontSize: 13, fontWeight: 600 }}>Caller name (optional)</label>
        <input
          type="text"
          value={callerName}
          onChange={(e) => setCallerName(e.target.value)}
          placeholder="e.g. Westfield reception"
          style={inputStyle}
        />

        <label style={{ fontSize: 13, fontWeight: 600 }}>Site / location (optional)</label>
        <input
          type="text"
          value={siteName}
          onChange={(e) => setSiteName(e.target.value)}
          placeholder="e.g. Bondi Junction store"
          style={inputStyle}
        />

        <label style={{ fontSize: 13, fontWeight: 600 }}>Skill</label>
        <select
          value={skillId}
          onChange={(e) => setSkillId(e.target.value)}
          required
          style={inputStyle}
        >
          <option value="">Choose a skill...</option>
          {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <label style={{ fontSize: 13, fontWeight: 600 }}>What's the issue?</label>
        <textarea
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          required
          placeholder="What the caller said — symptoms, urgency, location..."
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />

        {error && <div className="error-banner">{error}</div>}

        <button
          type="submit"
          disabled={busy || !skillId || !description.trim()}
          className="btn-primary"
        >
          {busy ? 'Dispatching...' : 'Create + dispatch'}
        </button>
      </form>
    </div>
  );
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 14,
};
